/**
 * Derive a Tarot-style Roman numeral from a card's name/id/number.
 * Based on Osho Zen Tarot + standard Rider-Waite numbering.
 * - Major Arcana: 0 → "0", 1 → "I", ... 21 → "XXI"
 * - Minor Arcana: Ace → "I", Two/Deux → "II", …, Ten → "X"; Court cards → "Pg/Kn/Q/K"
 * Returns '' if it cannot infer.
 */

const MAJOR_ARCANA = {
  // Standard Rider-Waite
  'the fool': 0, 'fool': 0,
  'the magician': 1, 'magician': 1,
  'the high priestess': 2, 'high priestess': 2, 'the priestess': 2,
  'the empress': 3, 'empress': 3,
  'the emperor': 4, 'emperor': 4,
  'the hierophant': 5, 'hierophant': 5,
  'the lovers': 6, 'lovers': 6,
  'the chariot': 7, 'chariot': 7,
  'strength': 8,
  'the hermit': 9, 'hermit': 9,
  'wheel of fortune': 10, 'the wheel of fortune': 10, 'wheel': 10,
  'justice': 11,
  'the hanged man': 12, 'hanged man': 12,
  'death': 13,
  'temperance': 14,
  'the devil': 15, 'devil': 15,
  'the tower': 16, 'tower': 16,
  'the star': 17, 'star': 17,
  'the moon': 18, 'moon': 18,
  'the sun': 19, 'sun': 19,
  'judgement': 20, 'judgment': 20,
  'the world': 21, 'world': 21,
  // Osho Zen equivalents (renamed majors)
  'existence': 21,
  'awakening': 20,
  'completion': 19,
  'past lives': 18,
  'silence': 17,
  'thunderbolt': 16,
  'conditioning': 15,
  'integration': 14, 'the master': 14,
  'transformation': 13,
  'new vision': 12,
  'breakthrough': 11,
  'change': 10,
  'aloneness': 9,
  'courage': 8,
  'awareness': 7,
  'the lovers oz': 6,
  'no-thingness': 5,
  'the rebel': 4,
  'creativity': 3,
  'inner voice': 2,
  'existence oz': 21,
};

const MINOR_RANKS = {
  'ace': 1, 'one': 1,
  'two': 2, 'deux': 2, 'dos': 2,
  'three': 3, 'trois': 3, 'tres': 3,
  'four': 4, 'cinco': 5,
  'five': 5,
  'six': 6, 'seven': 7, 'eight': 8, 'nine': 9, 'ten': 10,
};

const COURT_CODES = {
  'page': 'Pg', 'knight': 'Kn', 'queen': 'Q', 'king': 'K',
  // Osho Zen court
  'master': 'M', 'servant': 'Sv', 'seeker': 'Sk',
};

const ROMAN = ['', 'I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X',
  'XI', 'XII', 'XIII', 'XIV', 'XV', 'XVI', 'XVII', 'XVIII', 'XIX', 'XX', 'XXI'];

export function toRoman(n) {
  if (n === 0 || n === '0') return '0';
  if (typeof n !== 'number' || n < 0 || n > 21) return '';
  return ROMAN[n] || '';
}

export function cardRoman(card) {
  if (!card) return '';
  // 1. Explicit number field
  if (typeof card.number === 'number') return toRoman(card.number);
  if (typeof card.arcana_number === 'number') return toRoman(card.arcana_number);
  const name = String(card.name || '').trim().toLowerCase();
  if (!name) return '';
  // 2. Major arcana by name
  if (MAJOR_ARCANA[name] !== undefined) return toRoman(MAJOR_ARCANA[name]);
  // Try stripping "the " prefix
  const stripped = name.replace(/^the\s+/, '');
  if (MAJOR_ARCANA[stripped] !== undefined) return toRoman(MAJOR_ARCANA[stripped]);
  // 3. Minor arcana: "Two of Cups", "Ace of Wands"
  const firstWord = name.split(/\s+/)[0];
  if (MINOR_RANKS[firstWord]) return toRoman(MINOR_RANKS[firstWord]);
  if (COURT_CODES[firstWord]) return COURT_CODES[firstWord];
  // 4. Numeric prefix "5 of Cups"
  const numMatch = name.match(/^(\d+)/);
  if (numMatch) {
    const n = parseInt(numMatch[1], 10);
    if (n >= 1 && n <= 10) return toRoman(n);
  }
  return '';
}
